import { createFileRoute, Link, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import React, { Suspense } from 'react';
import { Image } from '../-components/image';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { useNgoProfileQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import {
	DocumentTextIcon,
	LoginIcon,
	MessageQuestionIcon,
	NoteIcon,
	SettingIcon,
} from './-components/assets/sidebar-icons';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
} from '@worldcoo/ui/components/sidebar';
import { DraftBanner } from './-components/draft-banner';
import { FaqPopup } from './-components/faq-popup';
import { MainSection } from './-components/main-section';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { navigateWithViewTransition } from '@/lib/view-transition-utils';
import { FormTranslationsProvider } from '../-components/form-translations-provider';

export const Route = createFileRoute('/ngos')({
	component: RouteComponent,
});

function RouteComponent() {
	const isLoginPage = useMatch({ from: '/ngos/login', shouldThrow: false });
	const isOnboardingPage = useMatch({ from: '/ngos/onboarding', shouldThrow: false });
	const isSelectNgoPage = useMatch({ from: '/ngos/select-ngo', shouldThrow: false });
	const isAuthCallbackPage = useMatch({ from: '/ngos/auth/callback', shouldThrow: false });
	const isFaqPage = useMatch({ from: '/ngos/faq', shouldThrow: false });

	// Public pages render their own layout (no sidebar)
	if (isLoginPage || isOnboardingPage || isSelectNgoPage || isAuthCallbackPage) {
		return (
			<I18nProvider i18n={i18n}>
				<FormTranslationsProvider>
					<Outlet />
				</FormTranslationsProvider>
			</I18nProvider>
		);
	}

	return (
		<I18nProvider i18n={i18n}>
			<FormTranslationsProvider>
				<SidebarProvider
					className="h-screen bg-[#fff7ed]"
					style={{ '--primary': '#f28e41', '--sidebar-width': '192px' } as React.CSSProperties}
				>
					<NgoSidebar />
					<MainSection>
						<DraftBanner />
						<Outlet />
					</MainSection>

					{!isFaqPage && <FaqPopup />}
				</SidebarProvider>
			</FormTranslationsProvider>
		</I18nProvider>
	);
}

function NgoSidebar() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mutate: logout } = useMutation(
		trpc.auth.logout.mutationOptions({
			onSuccess: () => {
				queryClient.removeQueries({ queryKey: trpc.ngo.pathKey() });
				navigateWithViewTransition(
					() => navigate({ to: '/ngos/login' }),
					() => !document.querySelector('[data-slot="sidebar"]'),
				);
			},
		}),
	);

	return (
		<Sidebar collapsible="none" className="bg-transparent" style={{ viewTransitionName: 'ngo-sidebar' }}>
			<SidebarHeader className="px-3 py-4">
				<Suspense fallback={<SidebarLogoSkeleton />}>
					<SidebarLogo />
				</Suspense>
			</SidebarHeader>

			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu className="gap-1">
							<NgoMenuItem label={t`Projects`} icon={DocumentTextIcon} to="/ngos/projects" />
							<NgoMenuItem label={t`My Portals`} icon={NoteIcon} to="/ngos/portals" />
							<NgoMenuItem label={t`Settings`} icon={SettingIcon} to="/ngos/settings" />
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter className="px-2 pb-4">
				<SidebarMenu className="gap-1">
					<NgoMenuItem label={t`FAQs`} icon={MessageQuestionIcon} to="/ngos/faq" />
					<SidebarMenuItem>
						<SidebarMenuButton variant="portal" size="portal" asChild>
							<button type="button" onClick={() => logout()} className="cursor-pointer">
								<NgoMenuIcon icon={LoginIcon} />
								<span>{t`Log Out`}</span>
							</button>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>
		</Sidebar>
	);
}

function NgoMenuItem(props: {
	label: string;
	icon: React.ComponentType<React.SVGAttributes<SVGSVGElement>>;
	to: '/ngos/projects' | '/ngos/portals' | '/ngos/faq' | '/ngos/settings';
}) {
	return (
		<SidebarMenuItem>
			<SidebarMenuButton variant="portal" size="portal" asChild>
				<Link to={props.to}>
					<NgoMenuIcon icon={props.icon} />
					<span>{props.label}</span>
				</Link>
			</SidebarMenuButton>
		</SidebarMenuItem>
	);
}

function NgoMenuIcon(props: { icon: React.ComponentType<React.SVGAttributes<SVGSVGElement>> }) {
	const Icon = props.icon;
	return (
		<div className="flex size-8 shrink-0 items-center justify-center rounded-lg p-1 transition-colors group-data-[status=active]/menu-button:bg-[#f28e41]">
			<Icon className="!size-5 text-[#1d1d1b] transition-colors group-data-[status=active]/menu-button:text-white" />
		</div>
	);
}

function SidebarLogo() {
	const ngo = useNgoProfileQuery();

	return (
		<div className="flex items-center gap-2">
			{ngo.logo && typeof ngo.logo === 'string' ? (
				<div className="border-portal-border flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white">
					<Image
						src={ngo.logo}
						alt={ngo.name ?? ''}
						height={24}
						aspectRatio={1}
						objectFit="contain"
						className="size-6 object-contain"
					/>
				</div>
			) : (
				<div className="border-portal-border flex size-8 shrink-0 items-center justify-center rounded-lg border bg-white text-xs font-bold text-[#f28e41]">
					{ngo.name?.charAt(0)?.toUpperCase() ?? 'N'}
				</div>
			)}
			<span className="truncate text-base font-medium text-[#1d1d1b]">{ngo.name ?? 'NGO'}</span>
		</div>
	);
}

function SidebarLogoSkeleton() {
	return (
		<div className="flex items-center gap-2">
			<Skeleton className="size-8 rounded-lg" />
			<Skeleton className="h-5 w-16" />
		</div>
	);
}
